/*  MUI  */
import { Box, Grid } from '@mui/material';
import { withTheme } from '@mui/styles';

/*  Shared  */
import { Sum } from 'application/Shared/Components/Cards';
import { DataCard } from 'application/Shared/Components/Cards/Components/DataCard';
import { DataAPIVisualsPieChart } from 'application/Shared/Components/DataAPIVisualsCard';
import { TooltipQ } from 'application/Shared/Components/Tooltips';
import { Helpers, Response, Route } from 'application/Shared/Helpers';
import { getScoreFillColor } from 'application/Shared/Reports/ScoreFillColor';
import { Requests, User } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import equal from 'fast-deep-equal';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { withRouter } from 'router/withRouter';

/*  Constants  */
const { hasData, hasResponseData, getData, getCount, getProperty } = Response;

class Insights extends Component {
    constructor(props) {
        super(props);
        this.state = {
            breachCount: 0,
            clientId: '',
            genericCreds: 0,
            route: '',
            score: 0,
            sharedCreds: 0,
            totalApps: [
                { name: 'Classified', value: 0 },
                { name: 'Unclassified', value: 0 }
            ],
            userCount: 0
        };

        const clientId = props?.match?.params?.clientId;
        /*  Requests  */
        this.requests = new Requests();
        this.User = new User(clientId);
    }

    componentDidMount() {
        const { match } = this.props;
        const { clientId, partnerId } = match.params;
        const route = Route.buildBaseAdminRoute({ clientId, partnerId });
        this.setState({ clientId, route }, () => {
            this.load();
        });
    }

    componentDidUpdate(prevProps) {
        const { dateSearch } = this.props;
        if (!equal(dateSearch, prevProps.dateSearch)) {
            this.loadFilteredData();
        }
    }

    componentWillUnmount() {
        this.requests.cancel();
        this.User.cancel();
    }

    load = () => {
        this.loadTotalApps();
        this.loadUserCount();
        this.loadScore();
        this.loadFilteredData();
    };

    getClassified = async () => {
        const { clientId } = this.state;

        const params = { applicationId: '*', classified: true, count: true };
        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        try {
            const response = await axios.get('/tenantApp', {
                cancelToken: this.requests.token(),
                params
            });
            return getCount(response);
        } catch (e) {
            debug.e(e);
            return 0;
        }
    };

    getUnclassified = async () => {
        const { clientId } = this.state;

        const params = { applicationId: '*', classified: false, count: true };
        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        try {
            const response = await axios.get('/tenantApp', {
                cancelToken: this.requests.token(),
                params
            });
            return getCount(response);
        } catch (e) {
            debug.e(e);
            return 0;
        }
    };

    loadTotalApps = async () => {
        const { t } = this.props;
        const classified = await this.getClassified();
        const unclassified = await this.getUnclassified();
        this.setState({
            totalApps: [
                { name: t('Common.classified'), value: classified },
                { name: t('Common.unclassified'), value: unclassified }
            ]
        });
    };

    loadUserCount = async () => {
        try {
            const userCount = await this.User.getCount();
            this.setState({ userCount });
        } catch (e) {
            /* */
        }
    };

    loadScore = async () => {
        const { clientId } = this.state;

        const params = { linkedType: 'SaaSSophisticationScore' };
        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        try {
            const response = await axios.get('/calculation', {
                cancelToken: this.requests.token(),
                params
            });
            if (!hasResponseData(response)) {
                return;
            }

            const data = getProperty(response, 'data');
            let score = parseInt(data, 10);
            if (!Helpers.isNum(score)) {
                score = '-';
            }

            this.setState({ score });
        } catch (e) {
            debug.e(e);
        }
    };

    loadFilteredData = async () => {
        const { dateSearch } = this.props;
        if (!Helpers.isset(dateSearch, 'value')) {
            return;
        }

        const date = dateSearch.value;
        if (!date) {
            return;
        }

        this.loadBreaches(date);
        this.loadSharedCreds(date);
        this.loadGenericCreds(date);
    };

    loadBreaches = async (dateAnnounced) => {
        const { clientId } = this.state;

        const params = {
            dateAnnounced,
            includeClientsImpacted: true,
            includeOnlyThisTenant: true,
            size: -1
        };
        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        try {
            const response = await axios.get('/breach/', {
                cancelToken: this.requests.token(),
                params
            });
            if (!hasData(response)) {
                return;
            }

            let breachCount = 0;
            const breaches = getData(response);
            breaches.forEach((breach) => {
                const { clientsImpacted } = breach;
                if (!clientsImpacted) {
                    return;
                }

                breachCount += parseInt(clientsImpacted, 10);
            });
            this.setState({ breachCount });
        } catch (e) {
            debug.e(e);
        }
    };

    loadSharedCreds = async (dateCreated) => {
        const { clientId } = this.state;
        const event = 'login_SharedCredential';

        /* params */
        const params = { count: true, dateCreated, event };
        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        try {
            const response = await axios.get('/incident/', {
                cancelToken: this.requests.token(),
                params
            });
            this.setState({ sharedCreds: getCount(response) });
        } catch (e) {
            debug.e(e);
        }
    };

    loadGenericCreds = async (dateCreated) => {
        const { clientId } = this.state;
        const event = 'login_GenericCredential';

        /* params */
        const params = { count: true, dateCreated, event };
        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        try {
            const response = await axios.get('/incident/', {
                cancelToken: this.requests.token(),
                params
            });
            this.setState({ genericCreds: getCount(response) });
        } catch (e) {
            debug.e(e);
        }
    };

    renderTotalApps = () => {
        const { link, match, t } = this.props;
        const { totalApps } = this.state;

        const { partnerId, clientId } = match.params;
        const baseAdminRoute = Route.buildBaseAdminRoute({
            clientId,
            partnerId
        });

        return (
            <Link
                to={link || `${baseAdminRoute}/applications/insights`}
                className="link"
            >
                <DataCard
                    title={
                        <Box display="inline-flex" alignItems="center">
                            {t('Insights.total_apps_discovered')}
                            <TooltipQ
                                text={t('Tooltips.Insights.total_apps')}
                            />
                        </Box>
                    }
                >
                    <Grid
                        container
                        spacing={1}
                        alignItems="center"
                        textAlign="center"
                    >
                        <Grid item xs={3}>
                            <Sum sum={totalApps} noCard />
                        </Grid>
                        <Grid item xs={9}>
                            <DataAPIVisualsPieChart data={totalApps} />
                        </Grid>
                    </Grid>
                </DataCard>
            </Link>
        );
    };

    render() {
        const { t, theme, link, report, start, end } = this.props;
        const {
            userCount,
            score,
            breachCount,
            sharedCreds,
            genericCreds,
            route
        } = this.state;

        const xs = report ? 6 : 12;
        const sm = report ? 6 : 6;
        const lg = report ? 6 : 4;

        return (
            <Grid container spacing={3}>
                <Grid item xs={xs} sm={sm} lg={lg}>
                    {this.renderTotalApps()}
                </Grid>
                <Grid item xs={xs} sm={sm} lg={lg}>
                    <Sum
                        title={t('Common.total_users')}
                        sum={userCount}
                        link={link || `${route}/users`}
                    />
                </Grid>
                <Grid item xs={xs} sm={sm} lg={lg}>
                    <Sum
                        title={t('Common.health_score')}
                        sum={score}
                        color={
                            score >= 70 // Needs to be in sync with getScoreFillColor levels
                                ? theme.palette.saasScore.text.excellent
                                : getScoreFillColor({ theme, value: score })
                        }
                        link={link || route}
                    />
                </Grid>
                <Grid item xs={xs} sm={sm} lg={lg}>
                    <Sum
                        title={t('Insights.vendor_incidents')}
                        sum={breachCount}
                        link={link || `${route}/breaches`}
                    />
                </Grid>
                <Grid item xs={xs} sm={sm} lg={lg}>
                    <Sum
                        title={t('Insights.sharedCredsUsed')}
                        sum={sharedCreds}
                        link={
                            link ||
                            `${route}/logs?start=${start}&end=${end}&flag=sharedCred`
                        }
                    />
                </Grid>
                <Grid item xs={xs} sm={sm} lg={lg}>
                    <Sum
                        title={t('Insights.serviceCredsUsed')}
                        sum={genericCreds}
                        link={
                            link ||
                            `${route}/logs?start=${start}&end=${end}&flag=serviceCred`
                        }
                    />
                </Grid>
            </Grid>
        );
    }
}

export default withRouter(withTranslation()(withTheme(Insights)));
